import { FC } from 'react';

import { getLoadingCropType, resetImageComponentByUpscaleStatus, resetUpscaleStatus } from 'utils/clickme';
import { UpscaleStatus } from 'utils/constants/banner';

import cropModalCancelUpscaleButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/upscale/crop_modal_cancel_upscale_button_click';
import { ActionBar, BottomSheet, Button, Modal, Text, VSpacing } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { fetchCancelUpscale } from 'components/common/BannerImg/UpscaleCancelConfirmModal/fetchCancelUpscale';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import {
    setBannerImageAiImageQueueIdByType,
    setBannerImageAiImageQueuePositionByType,
    setBannerImageComponentByType,
    setBannerImageUpscaleStatusByType,
} from 'store/models/createBanner';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'BannerImg.UpscaleConfirmStopModal.title',
    content: 'BannerImg.UpscaleConfirmStopModal.content',
    cancel: 'BannerImg.UpscaleConfirmStopModal.cancel',
    confirm: 'BannerImg.UpscaleConfirmStopModal.confirm',
};

export const UpscaleCancelConfirmModal: FC<{
    isUpscaleCancelConfirmModalVisible: boolean;
    hideModal: () => void;
    callback?: () => void;
}> = ({ isUpscaleCancelConfirmModalVisible, hideModal, callback }) => {
    const images = useSelector((state) => state.createBanner.images);

    const trls = useTrls();
    const dispatch = useDispatch();

    const closeModal = () => {
        cropModalCancelUpscaleButtonClick({ buttonName: 'back' });
        hideModal();
    };

    const cancelUpscale = () => {
        cropModalCancelUpscaleButtonClick({ buttonName: 'interrupt' });
        const loadingCropType = getLoadingCropType(images);

        if (loadingCropType) {
            const upscaleStatus = images[loadingCropType].upscaleStatus as UpscaleStatus;
            const aiImageQueueId = images[loadingCropType].aiImageQueueId;

            if (aiImageQueueId) {
                void fetchCancelUpscale(aiImageQueueId);
            }
            dispatch([
                setBannerImageComponentByType({
                    cropType: loadingCropType,
                    component: resetImageComponentByUpscaleStatus(upscaleStatus),
                }),
                setBannerImageUpscaleStatusByType({
                    cropType: loadingCropType,
                    upscaleStatus: resetUpscaleStatus(upscaleStatus),
                }),
                setBannerImageAiImageQueueIdByType({ cropType: loadingCropType, aiImageQueueId: null }),
                setBannerImageAiImageQueuePositionByType({ cropType: loadingCropType, position: null }),
            ]);
        }

        hideModal();

        if (callback) {
            callback();
        }
    };

    const footer = (
        <ActionBar
            primaryActions={
                <>
                    <Button mode="secondary" style="accent" onClick={closeModal} stretched>
                        {trls(TrlKeys.cancel)}
                    </Button>
                    <Button
                        mode="primary"
                        style="accent"
                        data-qa="ai-generation-stop-confirm"
                        onClick={cancelUpscale}
                        stretched
                    >
                        {trls(TrlKeys.confirm)}
                    </Button>
                </>
            }
        />
    );

    return (
        <>
            <Modal
                visible={isUpscaleCancelConfirmModalVisible}
                onClose={closeModal}
                size="small"
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={closeModal} />}
                    />
                }
                footer={footer}
            >
                <Text style="secondary">{trls(TrlKeys.content)}</Text>
            </Modal>
            <BottomSheet visible={isUpscaleCancelConfirmModalVisible} onClose={closeModal} footer={footer}>
                <Text typography="title-4-semibold">{trls(TrlKeys.title)}</Text>
                <VSpacing default={8} />
                <Text style="secondary">{trls(TrlKeys.content)}</Text>
            </BottomSheet>
        </>
    );
};
